/**
 * The names a client matchmakes by.
 *
 * Part of the wire contract for exactly the reason `CLIENT_MESSAGE` is: the
 * server registers a handler under a string and the client asks for one under a
 * string, and two sides that disagree fail at runtime with a matchmaking error
 * rather than at compile time. Frozen constants, declared once, imported by both
 * — `apps/game` registers them, `apps/web` joins them, and neither types the
 * literal.
 *
 * These live beside the message names rather than in `apps/game` because
 * `apps/web` cannot import the server, and a name the client re-declared for
 * itself would be a second definition of the one thing that has to match.
 */
export const ROOM = Object.freeze({
    lobby: 'lobby',
    /** One dungeon floor, simulated authoritatively. The room the game is actually played in. */
    descent: 'descent',
});
/**
 * Who may find a run.
 *
 * Two words rather than a boolean, because a boolean has to be named from one
 * side and every reader then has to remember which — `private: false` and
 * `listed: true` are the same fact spelled two ways, and a client that guessed
 * wrong would advertise a run somebody meant to keep to themselves.
 *
 *   - **public** — the run appears in the directory (`RUNS_PATH`) and anybody
 *     holding an account may read its code out of the list and join it.
 *   - **private** — the run appears in no listing at all. Its code is the only
 *     way in, and the code is only ever handed to whoever created the run.
 *
 * **Private is what an absent or unrecognised value means**, and `apps/game`
 * decides that rather than this package: a listing publishes the character
 * names of everybody in the party, so being advertised has to be something a
 * player asked for and never something that happened because a field was
 * missing (`apps/game`'s `resolveVisibility`).
 */
export const RUN_VISIBILITY = Object.freeze({
    public: 'public',
    private: 'private',
});
/**
 * The symbols an invite code is made of: Crockford's base 32.
 *
 * A code is meant to be **read out loud and typed back in**, which is a
 * different job from a room id and is why a room id will not do — `nMHVYK9pQ`
 * is nine characters of mixed case that nobody can dictate. Crockford's
 * alphabet is the standard answer to that job: the digits and the upper-case
 * letters, minus `I`, `L`, `O` and `U`. The first three are the ones a reader
 * confuses with `1` and `0`, so they are removed from the *code* space and
 * accepted on the way *in* instead (`parseInviteCode`); `U` goes with them,
 * which is Crockford's own rule and keeps an accidental word out of something
 * somebody has to say to a friend.
 *
 * Thirty-two symbols is also exactly five bits, and 256 is exactly eight of
 * them, which is what lets a code be drawn from raw bytes with no bias and no
 * rejection sampling (`apps/game`'s `mintInviteCode`).
 */
export const INVITE_CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
/**
 * How many symbols a code carries.
 *
 * Eight, which is 32⁸ = 2⁴⁰ ≈ 1.1 × 10¹² codes, and the arithmetic that has to
 * hold is *"a stranger cannot walk into a private run by trying"*. With `L`
 * runs alive at once, a single guess lands on one with probability `L / 2⁴⁰`,
 * so an attacker expects `2⁴⁰ / L` guesses per hit: at a hundred concurrent
 * runs that is 1.1 × 10¹⁰ tries — about 127 days at a thousand guesses a
 * second — and at ten thousand it is still 1.1 × 10⁸, or thirty hours at the
 * same rate. And the rate is not free: `apps/game` refuses to resolve a code
 * for a caller with no valid access token, so every one of those guesses costs
 * an account.
 *
 * Eight is also as long as a code can be and still be dictated in one breath.
 * If the game ever outgrows the numbers above, the lever is this constant —
 * each extra symbol multiplies the attacker's work by thirty-two — and not a
 * cleverer alphabet.
 */
export const INVITE_CODE_LENGTH = 8;
/**
 * The longest thing this will *look* at before deciding it is not a code.
 *
 * A code arrives as a path segment, so its length is the caller's to choose,
 * and normalising is string work. Thirty-two is four times a code, which leaves
 * room for every separator a person might type and refuses a megabyte of them
 * without touching it — the same posture `MAX_TOKEN_LENGTH` takes in
 * `apps/game`'s seat gate.
 */
export const MAX_INVITE_CODE_INPUT = 32;
/** Separators a person puts in a code to read it aloud, and the spaces around a paste. */
const SEPARATORS = /[\s-]/g;
/**
 * Crockford's decoding rule: the symbols the alphabet dropped, and what each
 * one was dropped in favour of. None of them can appear in a minted code, so
 * every spelling maps onto exactly one code and nothing is folded together.
 */
const SUBSTITUTIONS = { I: '1', L: '1', O: '0' };
/**
 * The code somebody typed, normalised — or `null`.
 *
 * Untrusted input like everything else this package parses: it arrives from a
 * URL a stranger wrote. Refusing is the ordinary outcome, so it is a returned
 * `null` rather than a throw.
 *
 * The tolerance is deliberate and it is what makes a code *shareable*: a player
 * reads eight characters to a friend over voice chat, the friend types them
 * back in lower case with a hyphen in the middle and an `O` where a zero was,
 * and that is the same code. What is **not** tolerated is a symbol the alphabet
 * never had (`U`, punctuation) or a length that is not eight — those are typos,
 * and answering a typo with somebody else's run is worse than refusing it.
 */
export const parseInviteCode = (value) => {
    if (typeof value !== 'string' || value.length > MAX_INVITE_CODE_INPUT)
        return null;
    const normalised = Array.from(value.replace(SEPARATORS, '').toUpperCase(), (symbol) => SUBSTITUTIONS[symbol] ?? symbol).join('');
    if (normalised.length !== INVITE_CODE_LENGTH)
        return null;
    if (!Array.from(normalised).every((symbol) => INVITE_CODE_ALPHABET.includes(symbol)))
        return null;
    return normalised;
};
/**
 * Where a client asks which runs are open.
 *
 * An ordinary HTTP route on the game server rather than a matchmaking call, and
 * that is a fact about the framework rather than a preference: Colyseus 0.17
 * exposes `POST /matchmake/:method/:roomName` and nothing else, and its SDK
 * dropped `getAvailableRooms` — so the room listing every version before it had
 * for free is now something a server writes for itself. Writing it is no loss:
 * what a stranger may be told about a run in progress is exactly the decision
 * this milestone is about, and a built-in listing would have answered it by
 * publishing whatever happened to be in the room's metadata.
 *
 * Both routes are **read with an access token**. See `RunListing` for what is
 * on the answer and why that is not nothing.
 */
export const RUNS_PATH = '/runs';
/**
 * Where a client turns an invite code into something it can join.
 *
 * The id is percent-encoded for the reason `internalSnapshotPath` encodes one:
 * the caller supplies an identifier, never a route.
 */
export const runPath = (code) => `${RUNS_PATH}/${encodeURIComponent(code)}`;
/**
 * Why the directory would not answer.
 *
 * Four, and the two in the middle of them are the reason this surface exists at
 * all: **a run that is full is not a run that never existed**, and neither of
 * them is a server that is down. Colyseus cannot tell a client those apart — its
 * matchmaker answers a locked room and a missing room with the same code — so a
 * party of four reported as a dead server is the failure this list is drawn to
 * prevent (`apps/web`'s `join-refusal.ts` has fixed the same shape twice).
 */
export const RUN_REFUSALS = Object.freeze([
    /** No access token, or one this server will not verify. Says nothing else, exactly as a refused seat does. */
    'unauthorised',
    /** Not the shape of a code at all — a typo, or a URL somebody made up. */
    'malformed-code',
    /** A well-formed code no live run is holding. It expired with the run, or it was never one. */
    'no-such-run',
    /** The code is a live run's, and the party is full. Wait, or find another one. */
    'run-full',
]);
/**
 * The HTTP status each refusal is sent with.
 *
 * Declared here, once, because both sides read it: the server writes the status
 * and a client that only looked at the status must reach the same conclusion as
 * one that read the body. `run-full` is a **409** for the reason
 * `CHARACTER_SEATED` is one — the request conflicts with a state that already
 * exists rather than with who is asking — and `no-such-run` is a plain 404, so
 * the two cannot be collapsed by a caller that branches on the status alone.
 */
export const RUN_REFUSAL_STATUS = Object.freeze({
    unauthorised: 401,
    'malformed-code': 400,
    'no-such-run': 404,
    'run-full': 409,
});
//# sourceMappingURL=rooms.js.map